/**
 * YouTube Embed with GA4 Tracking Support
 *
 * A lightweight YouTube embed that:
 * - Loads thumbnail initially (saves bandwidth)
 * - Loads full YouTube Player API iframe on click
 * - Tracks video interactions for Google Analytics 4
 *
 * GA4 Events Tracked:
 * - video_start: When video begins playing
 * - video_progress: At 25%, 50%, 75% completion
 * - video_pause: When user pauses
 * - video_complete: When video finishes
 */
class XEYoutubeEmbed extends HTMLElement {
  connectedCallback() {
    this.videoId = this.getAttribute('videoid');
    this.videoTitle = this.getAttribute('title') || 'YouTube video';
    this.customPoster = this.getAttribute('poster');
    this.progressTracked = { 25: false, 50: false, 75: false };

    // On mobile (≤768px), render native iframe directly for better UX
    if (window.innerWidth <= 768) {
      this.renderMobileIframe();
      return;
    }

    // Build thumbnail UI for desktop
    this.innerHTML = `
      <div class="xeyt-container">
        <img
          class="xeyt-thumbnail"
          alt="${this.videoTitle}"
          loading="lazy"
        >
        <button type="button" class="xeyt-playbtn" aria-label="Play: ${this.videoTitle}">
          <svg class="xeyt-playicon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="currentColor"/>
            <path d="M9 7.5L17 12L9 16.5V7.5Z" fill="white"/>
          </svg>
        </button>
      </div>
    `;

    // Load custom poster or YouTube thumbnail
    if (this.customPoster) {
      this.querySelector('.xeyt-thumbnail').src = this.customPoster;
    } else {
      this.loadThumbnail();
    }

    // Warm up the connection to YouTube
    this.addPrefetch('preconnect', 'https://www.youtube-nocookie.com');
    this.addPrefetch('preconnect', 'https://www.google.com');

    // Load YouTube IFrame API
    this.loadYouTubeAPI();

    // Activate player on click
    this.addEventListener('click', this.activate.bind(this), { once: true });
  }

  /**
   * Render native YouTube iframe on mobile (eliminates double-tap friction)
   */
  renderMobileIframe() {
    const params = this.getAttribute('params') || 'rel=0&cc_load_policy=1&hl=en';
    const embedUrl = `https://www.youtube.com/embed/${this.videoId}?${params}`;

    this.innerHTML = `
      <div class="xeyt-container">
        <iframe
          src="${embedUrl}"
          title="${this.videoTitle}"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          loading="lazy"
          style="width: 100%; aspect-ratio: 16 / 9; display: block;">
        </iframe>
      </div>
    `;
  }

  /**
   * Load the highest quality thumbnail available
   * Try maxresdefault first, fallback to sddefault if it fails
   */
  async loadThumbnail() {
    const img = this.querySelector('.xeyt-thumbnail');
    const qualities = [
      'maxresdefault', // 1280x720
      'sddefault',     // 640x480
      'hqdefault'      // 480x360
    ];

    for (const quality of qualities) {
      const url = `https://i.ytimg.com/vi/${this.videoId}/${quality}.jpg`;
      try {
        const response = await fetch(url, { method: 'HEAD' });
        if (response.ok) {
          img.src = url;
          return;
        }
      } catch (e) {
        // Try next quality
      }
    }

    // Fallback to default
    img.src = `https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg`;
  }

  /**
   * Add a <link rel={kind}> to the head for performance
   */
  addPrefetch(kind, url) {
    const linkElem = document.createElement('link');
    linkElem.rel = kind;
    linkElem.href = url;
    linkElem.crossOrigin = 'anonymous';
    document.head.append(linkElem);
  }

  /**
   * Load YouTube IFrame API script (loads once globally)
   */
  loadYouTubeAPI() {
    if (!window.YT && !document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  }

  /**
   * Replace thumbnail with YouTube Player API iframe
   */
  activate() {
    const container = this.querySelector('.xeyt-container');
    container.innerHTML = '<div class="xeyt-player"></div>';

    // Wait for API to be ready
    const initPlayer = () => {
      const params = this.getAttribute('params') || 'rel=0&cc_load_policy=1&hl=en';
      const playerVars = Object.fromEntries(new URLSearchParams(params));
      playerVars.autoplay = 1;

      this.player = new YT.Player(container.querySelector('.xeyt-player'), {
        videoId: this.videoId,
        width: '100%',
        height: '100%',
        playerVars: playerVars,
        events: {
          onReady: this.onPlayerReady.bind(this),
          onStateChange: this.onPlayerStateChange.bind(this)
        }
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      // Queue initialization for when API loads
      const originalCallback = window.onYouTubeIframeAPIReady || (() => {});
      window.onYouTubeIframeAPIReady = () => {
        originalCallback();
        initPlayer();
      };
    }
  }

  /**
   * Player ready - start tracking
   */
  onPlayerReady(event) {
    this.duration = event.target.getDuration();

    // Track video start in GA4
    this.trackEvent('video_start', {
      video_title: this.videoTitle,
      video_id: this.videoId,
      video_duration: Math.round(this.duration),
      video_provider: 'youtube'
    });

    // Start tracking progress milestones
    this.startProgressTracking();
  }

  /**
   * Track player state changes (pause, end, etc.)
   */
  onPlayerStateChange(event) {
    switch (event.data) {
      case YT.PlayerState.ENDED:
        this.trackEvent('video_complete', {
          video_title: this.videoTitle,
          video_id: this.videoId,
          video_provider: 'youtube'
        });
        // Clean up progress tracking
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
        }
        break;

      case YT.PlayerState.PAUSED:
        const currentTime = this.player.getCurrentTime();
        const percentWatched = Math.round((currentTime / this.duration) * 100);
        this.trackEvent('video_pause', {
          video_title: this.videoTitle,
          video_id: this.videoId,
          video_percent: percentWatched,
          video_current_time: Math.round(currentTime),
          video_provider: 'youtube'
        });
        break;
    }
  }

  /**
   * Track video progress milestones (25%, 50%, 75%)
   */
  startProgressTracking() {
    this.progressInterval = setInterval(() => {
      if (!this.player || typeof this.player.getCurrentTime !== 'function') {
        return;
      }

      const currentTime = this.player.getCurrentTime();
      const percent = (currentTime / this.duration) * 100;

      [25, 50, 75].forEach(milestone => {
        if (percent >= milestone && !this.progressTracked[milestone]) {
          this.progressTracked[milestone] = true;

          this.trackEvent('video_progress', {
            video_title: this.videoTitle,
            video_id: this.videoId,
            video_percent: milestone,
            video_current_time: Math.round(currentTime),
            video_provider: 'youtube'
          });
        }
      });

      // Stop tracking after 75%
      if (this.progressTracked[75]) {
        clearInterval(this.progressInterval);
      }
    }, 1000); // Check every second
  }

  /**
   * Send event to Google Analytics 4
   */
  trackEvent(eventName, params) {
    // Check for GA4 gtag function
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
      console.log(`[GA4 Video Tracking] ${eventName}:`, params);
    } else {
      console.warn('[GA4 Video Tracking] gtag not found - ensure GA4 is installed');
    }
  }

  /**
   * Cleanup when element is removed
   */
  disconnectedCallback() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
    if (this.player && typeof this.player.destroy === 'function') {
      this.player.destroy();
    }
  }
}

// Register the custom element
customElements.define('xe-youtube-embed', XEYoutubeEmbed);

export { XEYoutubeEmbed };
